import addMonths from 'date-fns/addMonths';

import fulfill from '../../fulfill';
import { SideEffectFunction } from '../../sideEffects';
import { CORE_API_HOSTNAME } from '../../env';
import { DraftOrder } from '../../types/orders';
import { ActionTypes, FetchNextResponse, FetchDraftsResponse, GetDraftsReason, GetDrafts } from './actions';
import { parseISOStrict, toISODatePartStr } from '../../helpers/date';

const limitFiltersToMaxThreeMonths = (queryParams: URLSearchParams) => {
    // Pass query params through unchanged for default listing, paging or a filter date range is set.
    const entries = [...queryParams.entries()];
    const hasNoParams = entries.length === 0;
    const hasOnlyPaging = entries.length === 1 && queryParams.get('starting_after');
    const hasOnlyLimit = entries.length === 1 && queryParams.get('limit');
    const hasOnlyPagingAndLimit = entries.length === 2 && queryParams.get('starting_after') && queryParams.get('limit');
    const hasRangeFilter = queryParams.get('created_at.gte') && queryParams.get('created_at.lte');
    if (hasNoParams || hasOnlyPaging || hasOnlyLimit || hasOnlyPagingAndLimit || hasRangeFilter) {
        return queryParams;
    }

    // Limit to last 3 months or 3 months if the params don't have any date filters.
    const threeMonthsAgo = addMonths(new Date(), -3);
    const hasNoRangeFilter = !queryParams.get('created_at.gte') && !queryParams.get('created_at.lte');
    if (hasNoRangeFilter) {
        const newParams = new URLSearchParams(queryParams);
        newParams.append('created_at.gte', toISODatePartStr(threeMonthsAgo));
        return newParams;
    }

    // If for some reason only the created_at.gte is set we add an lte for an upper bound.
    const hasOnlyGte = queryParams.get('created_at.gte');
    if (hasOnlyGte) {
        const gteDate = parseISOStrict(hasOnlyGte);
        if (gteDate) {
            // We  set an upper limit to the query params.
            const newParams = new URLSearchParams(queryParams);
            const threeMonthsAfterGte = addMonths(gteDate, 3);
            newParams.append('created_at.lte', toISODatePartStr(threeMonthsAfterGte));
            return newParams;
        } else {
            // The gteDate is not a date so the request will yield a 400 bad request response.
            return queryParams;
        }
    }

    // If for some reason only the created_at.lte is set we add a gte for a lower bound.
    const hasOnlyLte = queryParams.get('created_at.lte');
    if (hasOnlyLte) {
        const lteDate = parseISOStrict(hasOnlyLte);
        if (lteDate) {
            // We  set an lower limit to the query params.
            const newParams = new URLSearchParams(queryParams);
            const threeMonthsBeforeLte = addMonths(lteDate, -3);
            newParams.append('created_at.gte', toISODatePartStr(threeMonthsBeforeLte));
            return newParams;
        } else {
            // The lteDate is not a date so the request will yield a 400 bad request response.
            return queryParams;
        }
    }

    // Not really sure how we would end up here
    throw Error('Invalid queryParams when fetching transaction list');
};

const fetchDrafts: SideEffectFunction<GetDrafts, FetchDraftsResponse> = async (action) => {
    const params = limitFiltersToMaxThreeMonths(action.payload.params);
    const result: FetchDraftsResponse = await fulfill.get({
        accountId: action.payload.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${action.payload.accountId}/shopping/draft_orders?${params.toString()}`,
        handlers: {
            200: (response: { draft_orders: DraftOrder[] }) => ({
                type: ActionTypes.FetchDraftsResponse,
                payload: {
                    drafts: response.draft_orders,
                },
                meta: {
                    cause: action,
                },
            }),
            400: () => ({
                type: ActionTypes.FetchDraftsResponse,
                payload: {
                    drafts: [],
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const fetchNext: SideEffectFunction<FetchDraftsResponse, FetchNextResponse | undefined> = async (action) => {
    if (action.meta.cause.payload.reason === GetDraftsReason.refresh) {
        // no need to check next page if the causeing action was a "refresh"
        return;
    }
    const hasLimitInParams = action.meta.cause.payload.params.get('limit');
    const isAtLastPage = hasLimitInParams
        ? action.payload.drafts.length < parseInt(hasLimitInParams)
        : action.payload.drafts.length === 0;
    if (isAtLastPage) {
        return {
            type: ActionTypes.FetchNextResponse,
            payload: {
                drafts: [],
            },
            meta: {
                cause: action,
            },
        };
    }
    const params = limitFiltersToMaxThreeMonths(action.meta.cause.payload.params);
    const lastTransaction = action.payload.drafts[action.payload.drafts.length - 1];
    params.delete('starting_after');
    if (lastTransaction && lastTransaction.id) {
        params.append('starting_after', lastTransaction.id);
    }
    params.delete('limit');
    params.append('limit', '1');
    const result: FetchNextResponse = await fulfill.get({
        accountId: action.meta.cause.payload.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${
            action.meta.cause.payload.accountId
        }/shopping/draft_orders?${params.toString()}`,
        handlers: {
            200: (response: { draft_orders: DraftOrder[] }) => ({
                type: ActionTypes.FetchNextResponse,
                payload: {
                    drafts: response.draft_orders,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

// TODO: Draft details
// export const swapDraft: SideEffectFunction<DraftResponse, SwapDraft> = async (action) => {
//     return {
//         type: ActionTypes.SwapDraft,
//         payload: {
//             draft: action.payload.draft,
//         },
//         meta: {
//             cause: action,
//         },
//     };
// };

const effects = {
    [ActionTypes.GetDrafts]: fetchDrafts,
    [ActionTypes.FetchDraftsResponse]: fetchNext,
};

export default effects;
